import * as React from 'react';
import { useState } from 'react';
import config from '../../config/app';
import { Alert, Container, Form, FormGroup, Label, Row, Col } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import ValidationInput from '../common/ValidationInput';
import useValidation from '../../hooks/useValidation';
import AccountService from '../../services/AccountService';
import ProgressPage from '../common/ProgressPage';
import ProgressBar from '../common/ProgressBar';
import AnalyticsHelper from '../../helpers/AnalyticsHelper';
import { useIntl, FormattedMessage } from 'react-intl';
import useTrackPage from '../../hooks/useTrackPage';
import ValidationMessage from '../common/ValidationMessage';
import { Button, Checkbox, CircularProgress, FormControlLabel, FormLabel } from '@mui/material';

interface CreateAccountForm {
    firstName: string;
    lastName: string;
    organisation: string;
    emailAddress: string;
    password: string;
    confirmPassword: string;
    isTermsAndConditionsAccepted: boolean;
}

function CreateAccountPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const [createAccountError, setCreateAccountError] = useState('');
    const { register, errors, submitDisabled, submit, errorCount, formState, getValues } =
        useValidation<CreateAccountForm>();

    useTrackPage('Hub Account Creation page');

    const trackAccountCreation = (firstName: string, lastName: string, email: string, organisation: string) => {
        const userProperties = { firstName: firstName, lastName: lastName, email: email, organisation: organisation };
        AnalyticsHelper.identifyUserWithProperties({ email: email });
        AnalyticsHelper.trackClickWithProperties('Web Hub AcctCreate', userProperties);
    };

    const submitCreateAccount = (data: CreateAccountForm): Promise<void> => {
        return new Promise((resolve) => {
            AccountService.register(data.firstName, data.lastName, data.emailAddress, data.organisation, data.password)
                .then((response) => {
                    if ('errorMessage' in response) {
                        setCreateAccountError('Common.ErrorMessage');
                    } else {
                        trackAccountCreation(data.firstName, data.lastName, data.emailAddress, data.organisation);
                        navigate(config.verifyAccountPath, { state: { email: response.email } });
                    }
                })
                .catch((err) => {
                    setCreateAccountError(err);
                })
                .finally(() => {
                    resolve();
                });
        });
    };

    const validatePasswordMatch = (value: string): boolean | string => {
        if (value !== getValues('password')) {
            return intl.formatMessage({ id: 'Profile.CreateAccountPage.PasswordsMustMatch' });
        } else {
            return true;
        }
    };

    const trackRedirectToLoginPage = (): void => {
        AnalyticsHelper.trackClick('Web Hub AccountCreation SignIn');
    };

    const renderCreateAccountForm = (): JSX.Element => {
        return (
            <Form className="account-form--fixed-width" onSubmit={submit(submitCreateAccount)}>
                <div className="account-form__title">
                    <FormattedMessage id="Profile.CreateAccountPage.CreateAnAccountHeader" />
                </div>
                <div className="account-form__content">
                    {renderError()}

                    <fieldset disabled={formState.isSubmitting}>
                        <Row>
                            <Col>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="firstName">
                                            <FormattedMessage id="Profile.CreateAccountPage.FirstName" />
                                        </Label>
                                    </div>
                                    <ValidationInput
                                        testId="firstName"
                                        type="text"
                                        name="firstName"
                                        placeholder="First name"
                                        innerRef={register({
                                            required: intl.formatMessage({
                                                id: 'Profile.CreateAccountPage.FirstNameRequired',
                                            }),
                                        })}
                                        errors={errors}
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup>
                                    <div className="d-flex">
                                        <Label for="lastName">
                                            <FormattedMessage id="Profile.CreateAccountPage.LastName" />{' '}
                                        </Label>
                                    </div>
                                    <ValidationInput
                                        testId="lastName"
                                        type="text"
                                        name="lastName"
                                        placeholder="Last name"
                                        innerRef={register({
                                            required: intl.formatMessage({
                                                id: 'Profile.CreateAccountPage.LastNameRequired',
                                            }),
                                        })}
                                        errors={errors}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <div className="d-flex">
                                <Label for="email">
                                    <FormattedMessage id="Profile.CreateAccountPage.EmailAddress" />
                                </Label>
                            </div>
                            <ValidationInput
                                testId="emailAddress"
                                type="email"
                                name="emailAddress"
                                placeholder="youremail@domain.com"
                                innerRef={register({
                                    required: intl.formatMessage({
                                        id: 'Profile.CreateAccountPage.EmailRequired',
                                    }),
                                })}
                                errors={errors}
                            />
                        </FormGroup>
                        <FormGroup>
                            <div className="d-flex">
                                <Label for="lastName">
                                    <FormattedMessage id="Profile.CreateAccountPage.Organisation" />
                                </Label>
                            </div>
                            <ValidationInput
                                testId="organisation"
                                type="text"
                                name="organisation"
                                placeholder="Organisation name"
                                innerRef={register({
                                    required: intl.formatMessage({
                                        id: 'Profile.CreateAccountPage.OrganisationNameRequired',
                                    }),
                                })}
                                errors={errors}
                            />
                        </FormGroup>
                        <FormGroup>
                            <div className="d-flex">
                                <Label for="password">
                                    <FormattedMessage id="Profile.CreateAccountPage.Password" />
                                </Label>
                            </div>
                            <ValidationInput
                                testId="password"
                                type="password"
                                name="password"
                                placeholder="Password"
                                innerRef={register({
                                    required: intl.formatMessage({
                                        id: 'Profile.CreateAccountPage.PasswordRequired',
                                    }),
                                })}
                                errors={errors}
                                showPasswordToggle={true}
                            />
                        </FormGroup>
                        <FormGroup>
                            <div className="d-flex">
                                <Label for="confirmPassword">
                                    <FormattedMessage id="Profile.CreateAccountPage.ConfirmPassword" />
                                </Label>
                            </div>
                            <ValidationInput
                                testId="confirmPassword"
                                type="password"
                                name="confirmPassword"
                                placeholder="Confirm password"
                                innerRef={register({
                                    required: intl.formatMessage({
                                        id: 'Profile.CreateAccountPage.PasswordRequired',
                                    }),
                                    validate: validatePasswordMatch,
                                })}
                                errors={errors}
                                showPasswordToggle={true}
                            />
                        </FormGroup>
                        <FormGroup style={{ marginBottom: '0' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        data-testid="terms-and-privacy-checkbox"
                                        name="isTermsAndConditionsAccepted"
                                        inputRef={register({
                                            required: intl.formatMessage({
                                                id: 'Profile.CreateAccountPage.ToContinueTermsAndConditions',
                                            }),
                                        })}
                                    />
                                }
                                label={
                                    <FormLabel style={{ marginBottom: '0' }}>
                                        <span>
                                            <FormattedMessage id="Profile.CreateAccountPage.TermsAndConditionsDescription" />
                                        </span>
                                        <a href="https://resdiary.com/resdiary-api-license-terms">
                                            <FormattedMessage id="Profile.CreateAccountPage.TermsAndConditionsLink" />
                                        </a>
                                    </FormLabel>
                                }
                            />
                            {errorCount > 0 && errors.isTermsAndConditionsAccepted && (
                                <div className="validation-input-feedback">
                                    <ValidationMessage message={errors.isTermsAndConditionsAccepted.message!} />
                                </div>
                            )}
                        </FormGroup>

                        <FormLabel>
                            <div>
                                <span>
                                    <FormattedMessage id="Profile.CreateAccountPage.PrivacyNoticeDescription" />
                                </span>
                                <a href="https://resdiary.com/privacy-policy-b2b">
                                    &nbsp;
                                    <FormattedMessage id="Profile.CreateAccountPage.PrivacyNoticeLink" />
                                </a>
                            </div>
                        </FormLabel>
                    </fieldset>
                    <Button
                        type="submit"
                        disabled={submitDisabled || formState.isSubmitting}
                        variant="contained"
                        className="col submit"
                        data-testid="create-account-form-submit-button"
                        startIcon={
                            formState.isSubmitting && (
                                <CircularProgress
                                    data-testid="create-account-form-submit-button-spinner"
                                    color="inherit"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        transformOrigin: '12px 12px',
                                    }}
                                />
                            )
                        }
                    >
                        <FormattedMessage id="Common.CreateAccount" />
                    </Button>
                    <FormGroup>
                        <FormLabel className="d-flex align-items-center justify-content-center">
                            <FormattedMessage id="Login.LoginPage.HaveAnAccount" />
                            <a href={config.loginPath} onClick={() => trackRedirectToLoginPage()}>
                                &nbsp;
                                <FormattedMessage id="Common.LogIn" />
                            </a>
                        </FormLabel>
                    </FormGroup>
                </div>
            </Form>
        );
    };

    const renderError = (): JSX.Element | undefined => {
        if (createAccountError) {
            return (
                <Alert className="account-form__error">
                    <img src="/images/Alert-white.svg" alt="tick" />
                    <FormattedMessage id={createAccountError} />
                </Alert>
            );
        }
    };

    const progressBar = (): JSX.Element => {
        return (
            <ProgressPage
                pageTitle={intl.formatMessage({ id: 'Common.GetStartedMessage' })}
                progressBar={
                    <ProgressBar
                        createAccountStatus={'incomplete'}
                        organisationSetupStatus={'notStarted'}
                        accountDetailsStatus={'notStarted'}
                    />
                }
            />
        );
    };

    return (
        <Container className="account-page">
            <div className="flex-container-center">
                <div className="container">
                    {progressBar()}
                    {renderCreateAccountForm()}
                </div>
            </div>
        </Container>
    );
}

export default CreateAccountPage;
